import React, { useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { login, isAuthenticated } from "../../services/api/adminService";
import { ERROR_CODES } from "../../services/errors/ApiError";
import { ADMIN_HOME_PATH } from "./AdminGuard";
import "./admin.css";

/** Owner-facing wording for sign-in failures. */
const loginErrorMessage = (err) => {
  if (!err) return "";
  if (err.status === 401) return "That email and password don't match. Please try again.";
  if (err.status === 400) return "Please enter your email and password.";
  if (err.code === ERROR_CODES.SERVICE_UNAVAILABLE || err.status === 503) {
    return "Owner sign-in isn't set up on the server yet.";
  }
  return err.message || "Something went wrong. Please try again.";
};

// Only return to admin pages after sign-in, never to an arbitrary path.
const safeReturnPath = (from) =>
  typeof from === "string" && (from === ADMIN_HOME_PATH || from.startsWith(`${ADMIN_HOME_PATH}/`)) && from !== "/admin/login"
    ? from
    : ADMIN_HOME_PATH;

export default function AdminLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);

  const from = safeReturnPath(location.state && location.state.from);
  const expired = location.state && location.state.reason === "expired";

  if (isAuthenticated() && !submitting) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      setPassword("");
      navigate(from, { replace: true });
    } catch (err) {
      setError(loginErrorMessage(err));
      setSubmitting(false);
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <div className="admin">
      <main id="main" className="admin-login" tabIndex={-1}>
        <h1>Owner sign in</h1>
        <p className="admin-muted">Healing Hues bookings. This page is for the practice owner only.</p>

        {expired && !error && (
          <p className="admin-notice" role="status">Your session has ended. Please sign in again.</p>
        )}

        <form className="admin-form" onSubmit={onSubmit} noValidate>
          <label htmlFor="admin-email">Email</label>
          <input
            id="admin-email"
            name="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label htmlFor="admin-password">Password</label>
          <input
            id="admin-password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {error && (
            <p className="admin-error" role="alert">{error}</p>
          )}

          <button type="submit" className="admin-btn admin-btn--primary" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </main>
    </div>
  );
}
